'use client';

import React, { useEffect, useState } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';

interface AccordionSectionProps {
  id: string; // unique per section for sessionStorage
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

export function AccordionSection({ id, title, defaultOpen = true, children }: AccordionSectionProps) {
  const storageKey = `listing-accordion-${id}`;
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    const stored = sessionStorage.getItem(storageKey);

    if (stored !== null) setOpen(stored === 'true');
  }, [storageKey]);

  useEffect(() => {
    sessionStorage.setItem(storageKey, open ? 'true' : 'false');
  }, [open, storageKey]);

  return (
    <section className='bg-white'>
      <button
        type='button'
        className={[
          'w-full flex items-center justify-between px-6 py-2 bg-[#F8F8F7] focus:outline-none',
          'transition-colors',
        ].join(' ')}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={id + '-content'}
      >
        <span className='text-2xl font-bold text-left'>{title}</span>
        <span className='ml-2'>
          {open ? (
            <FiChevronDown className='w-5 h-5 text-gray-400' />
          ) : (
            <FiChevronRight className='w-5 h-5 text-gray-400' />
          )}
        </span>
      </button>
      <div
        id={id + '-content'}
        className={
          'overflow-hidden transition-all duration-300 ' + (open ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0')
        }
        aria-hidden={!open}
      >
        <div className='px-6 pt-4'>{children}</div>
      </div>
    </section>
  );
}
